import React, {useEffect, useRef, useState} from 'react';
import {BalloonEventModel, createStructuredEventData} from 'ts-core';
import {
    BalloonEventNotifier,
    CopyButton,
    EventDetailsPanel,
    Spinner,
    useIsMobile,
} from 'react-ui';
import {useLocation, useNavigate} from 'react-router-dom';
import {useBalloonEventsData} from '../contexts/EventData';
import {useMapBox} from '../components/BalloonEventsMapBox';
import {useBalloonEventsOptions} from '../contexts/EmbedOptions';
import {trackMixpanel} from '../utils/mixpanel';
import {CloseButton} from '../components/CloseButton.tsx';
import {CloseMobileDialogFooterButton} from '../components/CloseMobileDialogFooterButton.tsx';
import {EditButton} from '../components/EditButton.tsx';
import {CloneButton} from '../components/CloneButton.tsx';

export default function EventDetailPage() {
    const {openedEvent, ensureLoad, selectedEvent, deselectEvent: rawDeselectEvent} = useBalloonEventsData();
    const deselectEvent = () => {
        trackMixpanel('EventDetailPage', 'Close', {
            eventId: event?.id ?? '',
            eventName: event?.title ?? '',
        });
        rawDeselectEvent();
    };
    const eventSlug = openedEvent?.slug;
    const [event, setEvent] = useState<BalloonEventModel | null>(openedEvent);
    const mapBoxInfo = useMapBox();
    const map = mapBoxInfo?.map;
    const isMobile = useIsMobile();

    const {showDebug} = useBalloonEventsOptions();

    const initialized = useRef(false);
    const hasTrackedOpen = useRef<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const mountedPathRef = useRef<string | null>(null);

    // Close the sidebar when the user navigates *away* from the event detail
    // view — e.g. taps a tag/category badge inside the panel which routes to
    // `/tags/<slug>` or `/categories/<slug>`. Those `<Link>`s change the URL
    // but don't touch selectedEvent state, so without this the sidebar would
    // stay open on top of the newly-filtered map.
    //
    // We deliberately ignore path changes that stay inside `/event/…` (e.g.
    // the URL update issued by the new openedEvent→navigate effect in
    // EventDataProvider when this very panel opens) — otherwise the act of
    // opening the panel would immediately close it again.
    useEffect(() => {
        if (mountedPathRef.current === null) {
            mountedPathRef.current = location.pathname;
            return;
        }
        if (mountedPathRef.current !== location.pathname) {
            mountedPathRef.current = location.pathname;
            if (!location.pathname.startsWith('/event/')) {
                rawDeselectEvent();
            }
        }
    }, [location.pathname, rawDeselectEvent]);

    useEffect(() => {
        const listener = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // Clear `openedEvent` state — the URL-write effect in
                // EventDataProvider picks that up and resets the URL via
                // `replaceState` (browser mode) or `navigate('/')` (hash).
                // Using `navigate('/')` directly here would, in site/'s
                // browser mode, propagate to the shared BrowserRouter and
                // re-mount the homepage — same regression as clicking a tile.
                rawDeselectEvent();
            }
        };
        window.addEventListener('keydown', listener);
        const mapEl = document.querySelector<HTMLDivElement>('.balloon-events-map');
        const coords = selectedEvent?.getScreenCoordinates();
        if (mapEl && coords && coords.x && coords.x < 1100 && !isMobile) {
            mapEl.style.transform = 'translateX(650px)';
            mapEl.style.transition = 'transform 0.3s ease-out';
        }

        return () => {
            window.removeEventListener('keydown', listener);
            if (mapEl) {
                mapEl.style.transform = 'translateX(0px)';
            }
        };
    }, []);

    useEffect(() => {
        if (!map || initialized.current || !eventSlug) return;
        initialized.current = true;
        ensureLoad(eventSlug).then((e) => {
            if (!e) return;
            setEvent(e);
        });
    }, [map, eventSlug]);

    useEffect(() => {
        if (!event || !map) return;
        createStructuredEventData(event);
    }, [event, map]);

    useEffect(() => {
        if (event && hasTrackedOpen.current !== event.id) {
            hasTrackedOpen.current = event.id;
            trackMixpanel('EventDetailPage', 'Open', {
                eventId: event.id,
                eventName: event.title,
            });
        }
    }, [event]);

    // Re-fetch if the currently viewed event was just edited
    useEffect(() => {
        return BalloonEventNotifier.listen(async (payload) => {
            const matchesSlug = event?.slug && payload.slug === event.slug;
            const matchesId = event?.id && payload.id === event.id;
            if (matchesSlug || matchesId) {
                const updated = await ensureLoad(payload.slug || payload.id);
                if (updated) setEvent(updated);
            }
        });
    }, [event?.slug, event?.id, ensureLoad]);

    if (!event) {
        return (
            <div
                className="absolute w-full h-full top-0 lg:top-10 flex items-center flex-col pointer-events-none lg:max-h-[90%] max-h-[100%]">
                <div
                    className="p-10 shadow-lg rounded-lg w-[80%] lg:w-[500pt] bg-white pointer-events-auto justify-center items-center flex h-[100%]">
                    <Spinner/>
                </div>
            </div>
        );
    }

    // Mobile panel covers the embed in-place via `position: absolute` against
    // the embed root (`.be-embedded-app`, which is `position: relative`). The
    // previous `position: fixed` + `mapBoundingBox.{top,left,width,height}`
    // positioned in viewport coords — which (a) escaped the embed container
    // so host-page fixed chrome (e.g. dt-the7's left-side social bar on
    // csr-dialogforum.com) stacked above the panel, and (b) went stale on
    // scroll because the bounding box only re-measures on window resize.
    // Using absolute + 100% width/height keeps the panel pinned to the embed
    // through scroll, resize, and host-page padding.
    const mobileStyles: React.HTMLAttributes<HTMLDivElement> = isMobile ? {
        style: {
            zIndex: 1000,
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
        },
    } : {};

    const showDebugFooter = import.meta.env.VITE_CI_COMMIT_REF_SLUG !== 'main' || showDebug;

    return (
        <div
            {...mobileStyles}
            className="event-details slide-in-left absolute h-full top-0 left-[390px] w-[690px] flex flex-col bg-white backdrop-blur-md shadow-[2px_0px_6px_2px_rgba(0,0,0,0.20)]"
        >
            <div className="close-button-container absolute z-50 justify-center items-center p-0 border-0 border-gray-200 rounded-full top-4 right-6 backdrop-blur-sm bg-black/30">
                <CloseButton onClick={() => deselectEvent()}/>
            </div>

            <div className="overflow-y-scroll flex-1 event-details-content pointer-events-auto desc">
                <EventDetailsPanel
                    event={event}
                    trackingSource="map-sidebar"
                    showMicroMap={false}
                    showExploreCta={false}
                    extraActions={
                        <>
                            <EditButton slug={event.slug} canEdit={event.canEdit}/>
                            <CloneButton event={event}/>
                        </>
                    }
                    footerSlot={
                        showDebugFooter ? (
                            <div className="tools-row w-full flex flex-col mt-2">
                                <div className="w-full flex flex-row justify-between copy-button relative">
                                    <CopyButton content={event.id}>
                                        <span>{event.id}</span>
                                    </CopyButton>
                                </div>
                            </div>
                        ) : null
                    }
                />
            </div>

            {isMobile && (
                <div className="flex flex-row items-center px-4 py-2 w-full border-t border-gray-200">
                    <CloseMobileDialogFooterButton onClick={() => deselectEvent()}/>
                </div>
            )}
        </div>
    );
}
